<template>
  <div class="inputfield" :class="{ '-dark': dark }">
    <label
      v-if="label"
      :for="id"
      class="label -ellipse"
      :class="{ '-small': smallLabel }"
      >{{ label }}</label
    >
    <div class="-p-relative">
      <div class="-p-relative w-100">
        <slot name="prefix"></slot>
        <input
          :type="type"
          :id="id"
          :name="id"
          :value="value"
          @input="$emit('change', $event.target.value)"
          @focus.stop="$emit('focus')"
          @blur.stop="$emit('blur', $event.target.value)"
          :placeholder="placeholder"
          :style="style"
          :required="required"
          :min="min"
          :max="max"
          :maxLength="maxLength"
          :spellcheck="spellcheck"
          :autocomplete="autocomplete"
          :class="{ error: error, '-block': block }"
          :tabindex="tabindex"
          :disabled="disabled"
          :aria-label="ariaLabel"
          :aria-required="ariaRequired"
        />
        <SvgComponent
          v-if="loading"
          class="search-loader"
          :icon="{
            name: 'Loading',
            color: Globals.colors.greenDark,
            size: 18,
          }"
        />
        <span
          v-if="passwordToggler"
          :aria-hidden="true"
          class="password-toggler font-responsive-small -clickable -blue"
        >
          <button
            class="-stripped"
            v-if="type === 'password'"
            @click="$emit('on-view-password', true)"
          >
            {{ insertString("Global", "ShowPassword") }}
          </button>
          <button
            class="-stripped"
            v-if="type === 'text'"
            @click="$emit('on-view-password', false)"
          >
            {{ insertString("Global", "HidePassword") }}
          </button>
        </span>
      </div>
      <slot name="suffix"></slot>
    </div>
    <div
      :for="id"
      v-if="info"
      class="info font-responsive-small -small m-t-6 -gray-dark"
    >
      {{ info }}
    </div>
    <span v-if="error" role="alert" class="error font-responsive-small">{{
      errorMessage
    }}</span>
  </div>
</template>

<script>
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
export default {
  name: 'InputField',
  components: {
    SvgComponent
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    ariaRequired: {
      type: Boolean,
      default: false
    },
    /**
     * Gives the field this ID and Name.
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Prefills the input with existing values, using V-Model.
     */
    value: {
      type: [String, Number]
    },
    /**
     * The label to be displayed above the field.
     */
    label: {
      type: String,
      required: false
    },
    /**
     * 'True' renders a smaller version of the label.
     */
    smallLabel: {
      type: Boolean,
      default: true
    },
    /**
     * Adds a placeholder value.
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Sets a type for the input, valid values are; 'email', 'password', 'search', 'tel', 'text' and 'url'.
     */
    type: {
      type: String,
      default: 'text'
    },
    /**
     * Shows as information text below the field.
     */
    info: {
      type: String
    },
    /**
     * Displays the errorMessage, and changes the field border to red.
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the error message to be displayed if Error is set to True.
     */
    errorMessage: {
      type: String,
      default: ''
    },
    /**
     * Defines the width of the field. Overwritten by 'block' prop.
     */
    expectedChars: {
      type: Number,
      default: 16
    },
    /**
     * Makes the input field fill available space in parent. Overwrites 'expectedchars' prop.
     */
    block: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the HTML property.
     */
    required: {
      type: Boolean,
      default: false
    },
    min: [Number, String],
    max: [Number, String],
    maxLength: [Number, String],
    /**
     * Sets the HTML property.
     */
    spellcheck: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the HTML property.
     */
    autocomplete: {
      type: String,
      default: 'off'
    },
    /**
     * Sets the HTML property.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    tabindex: String,
    dark: Boolean,
    overrideBorderColor: {
      type: String
    },
    overrideFontColor: {
      type: String
    },
    loading: Boolean,
    passwordToggler: Boolean,
    ariaLabel: String
  },
  computed: {
    style () {
      const style = {};
      if (!this.block) {
        style.width = `${this.expectedChars * 9 + 18 * 2}px`;
      }
      if (this.overrideBorderColor) {
        style.borderColor = this.overrideBorderColor;
      }
      if (this.overrideFontColor) {
        style.color = this.overrideFontColor;
      }
      return style;
    }
  }
};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";

.inputfield {
  display: flex;
  flex-direction: column;
  position: relative;
  .password-toggler,
  .search-loader {
    position: absolute;
    top: 50%;
    right: 18px;
    transform: translateY(-50%);
  }
  label {
    white-space: nowrap;
    &.-small {
      @include font-responsive(small);
    }

    &.label {
      margin-bottom: 6px;
    }

    &.info {
      margin: 6px 0 0;
    }
  }

  div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    input {
      height: rem(42px);
      border: 1px solid $black;
      border-radius: 6px;
      padding: 9px 18px 12px;
      margin: 0;
      transition: border-color 0.33s;

      .apple-device & {
        padding-top: rem(15px);
      }

      &.-block {
        flex-grow: 1;
      }

      &.error {
        border: 1px solid $red;
      }

      &:focus {
        outline: 0 !important;

        .user-is-tabbing & {
          box-shadow: $focusTabbing;
        }
      }
    }
  }

  span.error {
    color: $red;
    margin: 6px 0 0;
  }

  &.-dark {
    input {
      color: $white;
      border-color: currentColor;
      background-color: $black;
    }
  }
}
</style>
